import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarCheck2,
  CheckCircle2,
  ClipboardList,
  Clock3,
  ExternalLink,
  Phone,
  PhoneOff,
  Send,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import FollowupPicker from "./FollowupPicker";
import SmartScheduler from "./SmartScheduler";

function formatMoney(centavos, currency = "PHP") {
  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : `${currency} `;
  return symbol + Math.round((centavos ?? 0) / 100).toLocaleString("en-US");
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatTimer(seconds) {
  return `${pad2(Math.floor(seconds / 60))}:${pad2(seconds % 60)}`;
}

function followupDueAt(row) {
  if (row?.current_state !== "Follow-up Scheduled") return null;
  return row.followup_at || row.next_eligible_at || null;
}

function isFutureIso(iso) {
  if (!iso) return false;
  const due = new Date(iso).getTime();
  return Number.isFinite(due) && due > Date.now();
}

function formatDueDate(iso) {
  if (!iso) return "the scheduled follow-up date";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "the scheduled follow-up date";
  }
}

function formatBriefDate(iso, includeTime = false) {
  if (!iso) return "Not recorded";
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return "Not recorded";
  return date.toLocaleString(undefined, includeTime
    ? { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }
    : { month: "short", day: "numeric", year: "numeric" });
}

function attemptLabel(attempt) {
  if (!attempt) return "No previous contact";
  if (attempt.channel === "messenger" && attempt.outcome === "sent") return "Messenger sent";
  if (attempt.outcome === "no_reply") return "No Messenger reply";
  if (attempt.outcome === "no_answer") return "No answer";
  if (attempt.outcome === "wrong_number") return "Wrong number";
  if (attempt.outcome_detail === "booked") return "Booked from recall";
  if (attempt.outcome_detail === "wants_to_think") return "Patient will think about it";
  if (["declined", "not_interested"].includes(attempt.outcome_detail)) return "Not interested";
  if (attempt.outcome === "reached") return "Reached patient";
  return [attempt.channel, attempt.outcome].filter(Boolean).join(" · ") || "Contact logged";
}

function patientInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return `${parts[0]?.[0] || ""}${parts.length > 1 ? parts.at(-1)?.[0] || "" : ""}`.toUpperCase();
}

function tutorialDetailFor(row) {
  return {
    id: row.id,
    patient_id: row.patient_id,
    patient_name: row.patient_name || "Maria Santos",
    patient_phone: row.patient_phone || "0917 555 0188",
    patient_email: row.patient_email || "maria.santos@example.test",
    messenger_conversation_url: row.messenger_conversation_url || "https://m.me/tutorial-recall",
    diagnosing_doctor_name: row.diagnosing_doctor_name || "Reyes",
    diagnosed_at: row.diagnosed_at || "2026-07-14T01:00:00.000Z",
    procedure_summary: "Root canal treatment · tooth 46",
    unbooked_items: [
      {
        item_id: "tutorial-rct-46",
        procedure_name: "Root canal treatment",
        tooth_number: "46",
        surface: null,
        status: "diagnosed",
        fee_centavos: 1200000,
        duration_minutes: 60,
        uses_chair_setup: true,
      },
    ],
  };
}

function procedureLine(items, detail, row) {
  if (items.length > 0) {
    const first = items[0];
    const parts = [first.procedure_name];
    if (first.tooth_number) parts.push(`tooth ${first.tooth_number}`);
    const primary = parts.filter(Boolean).join(" · ");
    return items.length > 1 ? `${primary} + ${items.length - 1} more` : primary;
  }
  if (detail?.procedure_summary) return detail.procedure_summary;
  const count = Number(row?.procedure_count || 0);
  return `${count} procedure${count === 1 ? "" : "s"}`;
}

export default function LoggingPanel({
  row,
  currency,
  canWork,
  tutorialDemo = false,
  followUpStatus = null,
  onClose,
  onAfterAction,
  onTutorialOutcome,
}) {
  const [detail, setDetail] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [mode, setMode] = useState("gate");
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [followupAt, setFollowupAt] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadContext() {
      setLoadingDetail(true);
      if (tutorialDemo) {
        setDetail(tutorialDetailFor(row));
        setAttempts([]);
        setLoadingDetail(false);
        return;
      }

      const [detailResult, attemptsResult] = await Promise.all([
        supabase.rpc("get_recall_row", { p_recall_queue_id: row.id }),
        supabase.rpc("list_recall_attempts", { p_recall_queue_id: row.id }),
      ]);
      if (cancelled) return;

      if (detailResult.error) console.warn("[recall] get_recall_row:", detailResult.error.message);
      if (attemptsResult.error) console.warn("[recall] list_recall_attempts:", attemptsResult.error.message);
      setDetail(detailResult.data ?? null);
      setAttempts(attemptsResult.data ?? []);
      setLoadingDetail(false);
    }

    loadContext();
    return () => {
      cancelled = true;
    };
  }, [row, row.id, tutorialDemo]);

  useEffect(() => {
    if (mode !== "call") {
      setElapsed(0);
      return undefined;
    }
    setElapsed(0);
    const timer = setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [mode]);

  const phone = detail?.patient_phone || row.patient_phone || "";
  const messengerUrl = detail?.messenger_conversation_url || row.messenger_conversation_url || "";
  const hasMessengerUrl = Boolean(String(messengerUrl).trim());
  const firstName = String(row.patient_name || "").trim().split(/\s+/)[0] || "the patient";
  const dueAt = followupDueAt(row);
  const followupLocked = isFutureIso(dueAt);
  const callDisabled = !canWork || !phone || followupLocked;
  const workSummary = useMemo(
    () => procedureLine(detail?.unbooked_items ?? [], detail, row),
    [detail, row],
  );

  function reset() {
    setMode("gate");
    setSelectedOutcome(null);
    setFollowupAt(null);
    setDeclineReason("");
    setNotes("");
  }

  const callRpc = useCallback(async (params) => {
    const { error } = await supabase.rpc("log_recall_attempt", params);
    if (error) throw new Error(error.message ?? "Couldn't log the outcome.");
  }, []);

  function finishTutorial(outcome) {
    toast.success("Tutorial outcome logged locally.");
    onTutorialOutcome?.({ rowId: row.id, outcome });
    reset();
    onAfterAction?.();
  }

  async function handleImmediateOutcome(outcome) {
    if (!canWork || submitting || followupLocked) return;
    setSubmitting(true);
    try {
      if (tutorialDemo) {
        finishTutorial(outcome);
        return;
      }

      await callRpc({
        p_recall_queue_id: row.id,
        p_channel: "call",
        p_outcome: outcome,
        p_outcome_detail: null,
        p_notes: notes || null,
      });
      toast.success(
        outcome === "wrong_number"
          ? "Patient paused — please update their phone number."
          : "Logged — patient cooling for a few days.",
      );
      reset();
      onAfterAction?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMessengerSent() {
    if (!canWork || submitting) return;
    setSubmitting(true);
    try {
      if (tutorialDemo) {
        finishTutorial("messenger_sent");
        return;
      }
      await callRpc({
        p_recall_queue_id: row.id,
        p_channel: "messenger",
        p_outcome: "sent",
        p_outcome_detail: null,
        p_notes: notes || null,
      });
      toast.success("Message logged — waiting for reply.");
      reset();
      onAfterAction?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCoolDownNoReply() {
    if (!canWork || submitting) return;
    setSubmitting(true);
    try {
      if (tutorialDemo) {
        finishTutorial("no_reply");
        return;
      }
      await callRpc({
        p_recall_queue_id: row.id,
        p_channel: "messenger",
        p_outcome: "no_reply",
        p_outcome_detail: null,
        p_notes: notes || "No Messenger reply.",
      });
      toast.success("Moved to cooling down.");
      reset();
      onAfterAction?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function saveReachedOutcome() {
    if (!canWork || submitting || !selectedOutcome || selectedOutcome === "book") return;
    if (selectedOutcome === "think" && !followupAt) return;

    setSubmitting(true);
    try {
      if (tutorialDemo) {
        finishTutorial(selectedOutcome === "not_interested" ? "declined" : "wants_to_think");
        return;
      }

      if (selectedOutcome === "think") {
        await callRpc({
          p_recall_queue_id: row.id,
          p_channel: "call",
          p_outcome: "reached",
          p_outcome_detail: "wants_to_think",
          p_notes: notes || null,
        });
        const { error } = await supabase.rpc("set_recall_followup", {
          p_recall_queue_id: row.id,
          p_followup_at: followupAt,
          p_notes: notes || null,
        });
        if (error) throw new Error(error.message ?? "Couldn't save follow-up date.");
        toast.success(
          `Saved — follow up ${new Date(followupAt).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          })}.`,
        );
      } else {
        const reasonNotes = [declineReason, notes].filter(Boolean).join(" — ");
        await callRpc({
          p_recall_queue_id: row.id,
          p_channel: "call",
          p_outcome: "reached",
          p_outcome_detail: "declined",
          p_notes: reasonNotes || null,
        });
        toast.success("Removed from recall.");
      }

      reset();
      onAfterAction?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    function onKey(event) {
      const tag = String(event.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || event.target?.isContentEditable) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "Escape") {
        event.preventDefault();
        if (selectedOutcome) setSelectedOutcome(null);
        else if (mode !== "gate") reset();
        else onClose?.();
        return;
      }
      if (mode !== "call" || selectedOutcome) return;

      const key = event.key.toLowerCase();
      if (key === "b") setSelectedOutcome("book");
      if (key === "t") setSelectedOutcome("think");
      if (key === "n") handleImmediateOutcome("no_answer");
      if (key === "x") setSelectedOutcome("not_interested");
      if (key === "w") handleImmediateOutcome("wrong_number");
    }

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mode, selectedOutcome, submitting, followupLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const shellWidth = selectedOutcome === "book" ? "max-w-2xl" : "max-w-[380px]";

  return (
    <div
      className={`mx-auto w-full overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-2xl ${shellWidth}`}
      data-testid={`recall-panel-${row.id}`}
    >
      {mode === "gate" ? (
        <>
          <PatientHero
            row={row}
            currency={currency}
            workSummary={workSummary}
            loading={loadingDetail}
            onClose={onClose}
          />
          <div
            className="space-y-4 px-5 pb-5 pt-5"
            data-testid="recall-channel-gate"
            data-tutorial-id="recall-channel-gate"
          >
            <div data-testid="recall-context" data-tutorial-id="recall-context">
              <CallBrief detail={detail} row={row} attempts={attempts} currency={currency} loading={loadingDetail} workSummary={workSummary} />
            </div>

            {followUpStatus}

            {followupLocked ? (
              <DueNotice dueAt={dueAt} />
            ) : null}

            {row.contact_info_needs_update ? (
              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Update this patient's contact information before outreach.
              </div>
            ) : null}

            <a
              href={callDisabled ? undefined : `tel:${phone.replace(/[^\d+]/g, "")}`}
              role="button"
              aria-disabled={callDisabled}
              onClick={(event) => {
                if (callDisabled || tutorialDemo || !event.isTrusted) event.preventDefault();
                if (!callDisabled) setMode("call");
              }}
              data-testid="recall-channel-card-call"
              data-tutorial-id="recall-channel-card-call"
              className={`flex h-14 w-full items-center justify-center gap-2 rounded-2xl px-4 text-[17px] font-semibold text-white shadow-sm transition-colors ${
                callDisabled ? "cursor-not-allowed bg-slate-300" : "bg-teal-600 hover:bg-teal-700"
              }`}
            >
              <Phone className="h-5 w-5" />
              Call {firstName}
            </a>

            <button
              type="button"
              disabled={!canWork || !hasMessengerUrl}
              onClick={() => setMode("messenger")}
              data-testid="recall-channel-card-messenger"
              data-tutorial-id="recall-channel-card-messenger"
              className="mx-auto block text-[14px] font-medium text-slate-600 underline-offset-4 hover:text-teal-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-300"
            >
              {hasMessengerUrl ? "or send a message" : "No Messenger link saved"}
            </button>

            {row.no_messenger_reply ? (
              <button
                type="button"
                disabled={!canWork || submitting}
                onClick={handleCoolDownNoReply}
                className="mx-auto block text-[12px] font-medium text-amber-700 hover:text-amber-900 disabled:opacity-50"
              >
                No reply yet — move to Cooling Down
              </button>
            ) : null}
          </div>
        </>
      ) : null}

      {mode === "call" ? (
        <>
          <LiveCallHeader firstName={firstName} elapsed={elapsed} onClose={onClose} />
          <div className="space-y-4 px-5 pb-5 pt-4">
            <CallBrief detail={detail} row={row} attempts={attempts} currency={currency} loading={loadingDetail} workSummary={workSummary} compact />

            {followupLocked ? <DueNotice dueAt={dueAt} /> : null}

            {selectedOutcome === "book" ? (
              <div className="space-y-3">
                <BackToOutcomes onClick={() => setSelectedOutcome(null)} />
                <SmartScheduler
                  row={row}
                  channel="call"
                  notes={notes}
                  currency={currency}
                  tutorialDemo={tutorialDemo}
                  onBooked={(result) => {
                    reset();
                    onTutorialOutcome?.({ rowId: row.id, outcome: "booked", result });
                    onAfterAction?.();
                  }}
                />
              </div>
            ) : selectedOutcome ? (
              <ReachedOutcomeForm
                outcome={selectedOutcome}
                firstName={firstName}
                doctorName={row.diagnosing_doctor_name}
                followupAt={followupAt}
                declineReason={declineReason}
                notes={notes}
                submitting={submitting}
                onFollowupAt={setFollowupAt}
                onDeclineReason={setDeclineReason}
                onNotes={setNotes}
                onBack={() => setSelectedOutcome(null)}
                onSave={saveReachedOutcome}
              />
            ) : (
              <CallOutcomes
                firstName={firstName}
                disabled={!canWork || submitting || followupLocked}
                onBook={() => setSelectedOutcome("book")}
                onThink={() => setSelectedOutcome("think")}
                onNoAnswer={() => handleImmediateOutcome("no_answer")}
                onNotInterested={() => setSelectedOutcome("not_interested")}
                onWrongNumber={() => handleImmediateOutcome("wrong_number")}
              />
            )}
          </div>
        </>
      ) : null}

      {mode === "messenger" ? (
        <>
          <CompactHeader title={`Message ${firstName}`} onBack={reset} onClose={onClose} />
          <div className="space-y-4 px-5 pb-5 pt-5" data-testid="recall-messenger-flow" data-tutorial-id="recall-messenger-flow">
            <CallBrief detail={detail} row={row} attempts={attempts} currency={currency} loading={loadingDetail} workSummary={workSummary} compact />
            <MessengerActions
              messengerUrl={messengerUrl}
              submitting={submitting}
              disabled={!canWork}
              onSent={handleMessengerSent}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

function PatientHero({ row, currency, workSummary, loading, onClose }) {
  return (
    <div className="relative bg-teal-700 px-6 pb-6 pt-5 text-center text-white" data-testid="recall-patient-hero">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-teal-50 hover:bg-white/15 hover:text-white"
        aria-label="Close recall flow"
        data-testid="recall-flow-close"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/20 text-[20px] font-semibold ring-1 ring-white/10">
        {patientInitials(row.patient_name)}
      </div>
      <h3 className="mt-3 text-[20px] font-bold leading-tight">{row.patient_name || "Unnamed patient"}</h3>
      <p className="mt-1 text-[12px] font-medium leading-relaxed text-teal-50">
        {loading ? "Loading treatment..." : workSummary} · {formatMoney(row.value_centavos, currency)}
      </p>
    </div>
  );
}

function CallBrief({ detail, row, attempts, currency, loading, workSummary, compact = false }) {
  const items = detail?.unbooked_items ?? [];
  const doctorName = detail?.diagnosing_doctor_name || row.diagnosing_doctor_name;
  const recommendedAt = detail?.diagnosed_at || row.diagnosed_at || items[0]?.reviewed_at;
  const latestAttempt = attempts[0] || null;
  const latestNote = String(latestAttempt?.notes || "").trim();
  const recallNote = String(detail?.notes || row.notes || "").trim();

  return (
    <section className="rounded-2xl border border-teal-100 bg-teal-50/70 px-4 py-4 text-slate-950" data-testid="recall-call-brief">
      <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-teal-800">
        <ClipboardList className="h-4 w-4" />
        Call brief
      </div>
      {loading ? (
        <p className="text-[13px] leading-relaxed text-slate-500">Loading patient context...</p>
      ) : (
        <>
          <div className="space-y-3">
            <BriefFact label="Treatment">
              {items.length > 0 ? (
                <div className="space-y-2">
                  {items.map((item, index) => {
                    const location = [
                      item.tooth_number ? `Tooth ${item.tooth_number}` : null,
                      item.surface ? `Surface ${item.surface}` : null,
                    ].filter(Boolean).join(" · ");
                    return (
                      <div key={item.item_id || `${item.procedure_name}-${index}`} className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-slate-900">{item.procedure_name || "Unnamed procedure"}</div>
                          {location ? <div className="mt-0.5 text-[11px] text-slate-500">{location}</div> : null}
                        </div>
                        <div className="shrink-0 text-[12px] font-bold tabular-nums text-slate-700">{formatMoney(item.fee_centavos, currency)}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[13px] font-semibold text-slate-900">{workSummary} · {formatMoney(detail?.value_centavos ?? row.value_centavos, currency)}</div>
              )}
            </BriefFact>

            <div className={`grid gap-3 ${compact ? "grid-cols-1" : "grid-cols-2"}`}>
              <BriefFact label="Dentist" value={doctorName && doctorName !== "Unassigned" ? `Dr. ${doctorName.replace(/^Dr\.\s*/i, "")}` : "Not assigned"} />
              <BriefFact label="Recommended" value={formatBriefDate(recommendedAt)} />
            </div>

            <BriefFact
              label="Last contact"
              value={latestAttempt ? `${attemptLabel(latestAttempt)} · ${formatBriefDate(latestAttempt.attempted_at, true)}` : "No previous contact"}
            />

            {latestNote ? (
              <BriefNote label="Last contact note" value={latestNote} testId="recall-last-contact-note" />
            ) : null}
            {recallNote && recallNote !== latestNote ? (
              <BriefNote label="Recall note" value={recallNote} testId="recall-queue-note" />
            ) : null}
          </div>

          <div className="mt-3 rounded-xl border border-teal-200 bg-white/80 px-3 py-2 text-[12px] font-medium text-teal-900" data-testid="recall-call-goal">
            <span className="font-bold">Goal:</span> Confirm whether the patient is ready to schedule.
          </div>
        </>
      )}
    </section>
  );
}

function BriefFact({ label, value, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-teal-700">{label}</div>
      {children || <div className="mt-0.5 text-[13px] font-semibold leading-snug text-slate-900">{value}</div>}
    </div>
  );
}

function BriefNote({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-[12px] leading-relaxed text-slate-700">{value}</div>
    </div>
  );
}

function DueNotice({ dueAt }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-[12px] font-medium text-violet-800" data-testid="recall-followup-due-label">
      <Clock3 className="mt-0.5 h-4 w-4 shrink-0" />
      Due {formatDueDate(dueAt)}. Staff can review this card, but cannot log an outcome yet.
    </div>
  );
}

function LiveCallHeader({ firstName, elapsed, onClose }) {
  return (
    <div className="flex h-12 items-center justify-between bg-teal-700 px-4 text-white" data-testid="recall-call-header" data-tutorial-id="recall-call-header">
      <div className="flex min-w-0 items-center gap-2 text-[14px] font-semibold">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-rose-400" />
        <span className="truncate">On call with {firstName}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="tabular-nums text-[14px] font-semibold" data-testid="recall-call-timer">
          {formatTimer(elapsed)}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-full text-teal-50 hover:bg-white/15 hover:text-white"
          aria-label="End call and close"
          data-testid="recall-cancel-call"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function CallOutcomes({ firstName, disabled, onBook, onThink, onNoAnswer, onNotInterested, onWrongNumber }) {
  return (
    <div data-testid="recall-outcome-picker" data-tutorial-id="recall-step-1">
      <h4 className="mb-3 text-[17px] font-semibold text-slate-950">What did {firstName} say?</h4>
      <div className="space-y-2.5" data-tutorial-id="recall-step-2">
        <OutcomeButton icon={CalendarCheck2} tone="emerald" testId="recall-step2-book" tutorialId="recall-step2-book" disabled={disabled} onClick={onBook}>
          Yes, set an appointment
        </OutcomeButton>
        <OutcomeButton icon={Clock3} tone="amber" testId="recall-step2-think" tutorialId="recall-step2-think" disabled={disabled} onClick={onThink}>
          {firstName === "the patient" ? "They" : firstName} will think about it
        </OutcomeButton>
        <OutcomeButton icon={PhoneOff} tone="slate" testId="recall-step1-no_answer" tutorialId="recall-step1-no-answer" disabled={disabled} onClick={onNoAnswer}>
          No answer
        </OutcomeButton>
        <OutcomeButton icon={X} tone="rose" testId="recall-step2-not_interested" tutorialId="recall-step2-not-interested" disabled={disabled} onClick={onNotInterested}>
          Not interested
        </OutcomeButton>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={onWrongNumber}
        data-testid="recall-step1-wrong_number"
        data-tutorial-id="recall-step1-wrong-number"
        className="mx-auto mt-3 block text-[12px] font-medium text-slate-400 hover:text-rose-700 disabled:opacity-50"
      >
        Wrong number?
      </button>
    </div>
  );
}

function OutcomeButton({ icon: Icon, tone, testId, tutorialId, disabled, onClick, children }) {
  const tones = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    slate: "text-slate-600",
    rose: "text-rose-700",
  };
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      data-tutorial-id={tutorialId}
      className="flex h-14 w-full items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 text-left text-[15px] font-semibold text-slate-950 transition-colors hover:border-teal-200 hover:bg-teal-50/40 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Icon className={`h-5 w-5 shrink-0 ${tones[tone]}`} />
      {children}
    </button>
  );
}

function ReachedOutcomeForm({
  outcome,
  firstName,
  doctorName,
  followupAt,
  declineReason,
  notes,
  submitting,
  onFollowupAt,
  onDeclineReason,
  onNotes,
  onBack,
  onSave,
}) {
  const isThinking = outcome === "think";
  const doctor = doctorName && doctorName !== "Unassigned" ? `Dr. ${doctorName}` : "the doctor";
  return (
    <div className="space-y-3">
      <BackToOutcomes onClick={onBack} />
      {isThinking ? (
        <FollowupPicker firstName={firstName} value={followupAt} onChange={onFollowupAt} />
      ) : (
        <div className="space-y-2" data-testid="recall-decline-reason" data-tutorial-id="recall-decline-reason">
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
            “Got it — anything I can pass along to {doctor}?”
          </p>
          <label className="block text-[12px] font-semibold text-slate-700">
            Reason <span className="font-normal text-slate-400">(optional)</span>
          </label>
          <input
            type="text"
            value={declineReason}
            onChange={(event) => onDeclineReason(event.target.value)}
            placeholder="What did they say?"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
            data-testid="recall-decline-reason-input"
          />
        </div>
      )}
      <label className="block text-[12px] font-semibold text-slate-700">
        Notes <span className="font-normal text-slate-400">(optional)</span>
      </label>
      <textarea
        value={notes}
        onChange={(event) => onNotes(event.target.value)}
        rows={2}
        placeholder="What are they saying?"
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
        data-testid="recall-notes-input"
      />
      <button
        type="button"
        disabled={submitting || (isThinking && !followupAt)}
        onClick={onSave}
        className="h-11 w-full rounded-xl bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        data-testid="recall-save-outcome"
        data-tutorial-id="recall-save-outcome"
      >
        {submitting ? "Saving..." : isThinking ? "Save follow-up" : "Save and remove from recall"}
      </button>
    </div>
  );
}

function BackToOutcomes({ onClick }) {
  return (
    <button type="button" onClick={onClick} className="text-[12px] font-semibold text-teal-700 hover:text-teal-900">
      ← Back to call outcomes
    </button>
  );
}

function CompactHeader({ title, onBack, onClose }) {
  return (
    <div className="flex h-12 items-center justify-between bg-teal-700 px-4 text-white">
      <button type="button" onClick={onBack} className="text-[13px] font-semibold text-teal-50 hover:text-white">
        ← Back
      </button>
      <span className="text-[14px] font-semibold">{title}</span>
      <button type="button" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-white/15" aria-label="Close">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function MessengerActions({ messengerUrl, submitting, disabled, onSent }) {
  function openMessenger() {
    if (!messengerUrl) return;
    window.open(messengerUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={openMessenger}
        disabled={!messengerUrl}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-teal-600 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-slate-300"
        data-testid="recall-messenger-open"
      >
        <ExternalLink className="h-4 w-4" /> Open Messenger
      </button>
      <button
        type="button"
        disabled={disabled || submitting}
        onClick={onSent}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
        data-testid="recall-messenger-sent"
        data-tutorial-id="recall-messenger-sent"
      >
        {submitting ? <Send className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        {submitting ? "Saving..." : "Message sent — wait for reply"}
      </button>
    </div>
  );
}
